/** Colores de gajo disponibles (mapeados a tokens del design system). */
export type PrizeColor = 'coral' | 'mint' | 'sky' | 'peach' | 'lilac' | 'sand';

export const PRIZE_COLORS: PrizeColor[] = ['coral', 'mint', 'sky', 'peach', 'lilac', 'sand'];

/** Una zona horaria ofrecida en /config. */
export interface TimezoneOption {
  /** Identificador IANA. Es lo que se guarda en el JSON. */
  id: string;
  /** Nombre de plaza que ve el operador. */
  label: string;
}

/** Las plazas del evento. El id IANA es lo unico que importa al formatear. */
export const TIMEZONES: TimezoneOption[] = [
  { id: 'America/Los_Angeles', label: 'Las Vegas' },
  { id: 'America/New_York', label: 'New York' },
  { id: 'Europe/London', label: 'London' },
  { id: 'Europe/Madrid', label: 'Spain' },
];

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

/** Un tipo de premio extra. Todos los gajos dan Free Fuel; esto es lo que se suma. */
export interface Prize {
  id: string;
  /** Nombre completo del premio: lo que se ve al ganarlo. Vacio = desactivado. */
  label: string;
  /**
   * Texto corto para pintar en el gajo. En un gajo cabe poco y a la velocidad
   * a la que gira se lee menos: "JACKPOT!" se ve, "$20 to Spend Anywhere +
   * additional free fuel" no. Vacio = se pinta el `label`.
   */
  corto?: string;
  /** Cupo total de aciertos para todo el periodo de juego. */
  aciertos: number;
  color: PrizeColor;
}

/**
 * Un premio ya entregado. Es un registro de solo anadir: nace al girar y se
 * completa una unica vez con los datos del ganador. Nada en la aplicacion lo
 * borra ni lo reescribe; para eso hay que editar el JSON del servidor.
 */
export interface Award {
  /** Identificador del registro. Es lo que el ganador envia al reclamar. */
  id: string;
  prizeId: string;
  ts: string;
  /** Nombre del ganador. Ausente si cerro el modal sin rellenar nada. */
  name?: string;
  email?: string;
  /**
   * Autorizacion expresa para tratar sus datos. Sin ella no se guardan, asi
   * que en la practica todo registro con datos la lleva; se anota igualmente
   * porque es la prueba de que se dio, y `claimedTs` dice cuando.
   */
  consent?: boolean;
  /** Instante en el que dejo sus datos y marco la autorizacion. */
  claimedTs?: string;
}

/**
 * El "fichero JSON" completo. Vive como un unico valor en Cloudflare KV.
 * Superset del formato del spec: `aciertos_premio_N` -> `premios[]` para
 * soportar N tipos configurables.
 */
/**
 * Una jornada del evento: una franja en la que la ruleta reparte premios.
 * Un evento de dos dias son dos jornadas; entre ellas la ruleta sigue girando
 * pero solo da Free Fuel, y esas horas muertas no consumen presupuesto.
 */
export interface Day {
  inicio: string;
  fin: string;
}

export interface State {
  /**
   * Jornadas del evento. Es la fuente de verdad del calendario.
   * `fecha_hora_inicio` y `fecha_hora_fin` se derivan de aqui (la primera
   * apertura y el ultimo cierre) y se conservan para que un JSON del formato
   * antiguo se siga leyendo igual.
   */
  dias: Day[];
  fecha_hora_inicio: string;
  fecha_hora_fin: string;
  /**
   * Zona IANA en la que el operador lee y escribe las dos fechas de arriba.
   * No mueve el evento: los instantes se guardan siempre en UTC. Solo dice
   * con que reloj se miran en /config.
   */
  timezone: string;
  /** Semilla del reparto. Nunca se expone al cliente. */
  seed: string;
  premios: Prize[];
  awards: Award[];
}

/** Lo que se edita en /config (el estado sin el log ni la semilla). */
export type Config = Omit<State, 'awards' | 'seed'>;

/** Un gajo de la ruleta. */
export interface Segment {
  index: number;
  /** null = solo Free Fuel, sin extra. */
  prizeId: string | null;
  /** Nombre completo del premio, vacio si no lo hay. Es lo que anuncia el modal. */
  extra: string;
  /** Lo que se pinta en el gajo. Vacio = se pinta `extra`. */
  corto: string;
  color: PrizeColor;
}

/** Respuesta de POST /api/spin. */
export interface SpinResponse {
  segmentIndex: number;
  prizeId: string | null;
  extra: string;
  /** Instante en el que el servidor resolvio el giro. */
  ts: string;
  /**
   * Registro que el ganador debe completar con sus datos. null cuando no hay
   * nada que pedir: sin premio extra, o con un premio que no los pide.
   */
  claimId: string | null;
}

/** Cuerpo de POST /api/awards: los datos del ganador de un premio extra. */
export interface ClaimRequest {
  awardId: string;
  name: string;
  email: string;
  consent: boolean;
}

/** Respuesta de GET /api/wheel: lo minimo para pintar la ruleta. */
export interface WheelResponse {
  segments: Segment[];
  /** true si `now` cae dentro del periodo de juego. */
  active: boolean;
}

/** Como va un tipo de premio en una jornada concreta. */
export interface DayPacing {
  inicio: string;
  fin: string;
  /** Cupo de este tipo para esta jornada. */
  quota: number;
  /** Entregados dentro de esta jornada. */
  awarded: number;
  /** Los que ya deberian haber salido a estas alturas de la jornada. */
  due: number;
  /** Cupo sin repartir. Definitivo si la jornada ya cerro. */
  pending: number;
  /** `now` cae dentro de esta jornada. */
  live: boolean;
  /** La jornada ya termino: lo pendiente se queda sin repartir. */
  closed: boolean;
}

/** Ritmo de un tipo de premio, para el panel de /config. */
export interface Pacing {
  prizeId: string;
  label: string;
  /** Cupo de todo el evento: la suma de las jornadas. */
  total: number;
  /** Duracion de cada slot en ms, en la jornada en curso (o la proxima). */
  slotMs: number;
  /** Premios que ya deberian haber salido en la jornada en curso. */
  due: number;
  /** Entregados en todo el evento. */
  awarded: number;
  /** Cupo del evento aun sin repartir. */
  remaining: number;
  /** Ritmo de la jornada en curso (o la proxima). */
  perHour: number;
  /** Tope de entregas por hora: el doble del ritmo. */
  hourCap: number;
  days: DayPacing[];
}
